import { useEffect, useState } from "react";
import { ApiError, api, formatMoney } from "./api";
import { Alert, Badge, Box, Empty, Modal, Table, Th, Working } from "./ui";

/** One transfer from the bank's alerts, in or out, with what it was for. */
export interface AlertTransfer {
  readonly id: string;
  readonly direction: "in" | "out";
  readonly amountCents: number;
  readonly at: string;
  readonly description: string | null;
  readonly saleReference: string | null;
  readonly describedBy: string | null;
  readonly statementDay: string | null;
}

const when = (at: string): string =>
  new Date(at).toLocaleString("en-SG", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", timeZone: "Asia/Singapore" });

/**
 * The transfers OCBC's alert emails reported, in and out, and what each was for (Daren and D,
 * 19 October 2026: "after reasoning is given, where does the transaction reflect at? Since the
 * monthly statement isn't out, we want to log the incoming and outgoing transfer here first";
 * "for outgoing I want to quickly write down as detail as possible immediately after it is
 * done"). What is written shows here at once, and beside the statement line once the statement
 * holding it is imported.
 */
export function AlertLog(): React.ReactElement {
  const [transfers, setTransfers] = useState<AlertTransfer[] | undefined>();
  const [waiting, setWaiting] = useState(true);
  const [editing, setEditing] = useState<{ id: string; words: string } | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  /** Transfers out ticked to be combined into one (Daren, 22 October 2026). */
  const [picked, setPicked] = useState<string[]>([]);
  const [combining, setCombining] = useState<string | undefined>();

  async function load(): Promise<void> {
    try {
      setTransfers((await api.alertTransfers({ waiting })).transfers);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load the alerts");
    }
  }
  useEffect(() => {
    void load();
  }, [waiting]);

  async function save(transfer: AlertTransfer, words: string): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      if (transfer.direction === "out") await api.describeOutgoing(transfer.id, words, Boolean(words.trim()));
      else await api.describeReceipt(transfer.id, words);
      setEditing(undefined);
      await load();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not save it");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box
      title="From the OCBC alerts"
      count={transfers?.length ?? 0}
      actions={
        <label className="flex items-center gap-2 text-[0.75rem]">
          <input type="checkbox" className="form-check-input" checked={waiting} onChange={(event) => setWaiting(event.target.checked)} />
          Only those not on a statement yet
        </label>
      }
    >
      <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50">
        Every transfer the bank's alert emails reported, as it happened, and what it was for. Write it down while it is fresh. It shows
        here straight away, and beside its line once the statement with it is imported. Several emails for one transfer out: tick them
        and combine them into one.
      </p>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {notice ? <Alert tone="success" onClose={() => setNotice(undefined)}>{notice}</Alert> : null}
      {picked.length >= 2 ? (
        <div className="flex flex-wrap items-center gap-2 mb-3 text-[0.813rem]">
          <span>{picked.length} transfers out ticked.</span>
          <button type="button" className="ti-btn ti-btn-primary-full !py-1 !px-3 !text-[0.75rem] !mb-0" onClick={() => {
            const first = transfers?.find((one) => one.id === picked[0]);
            setCombining(first ? (first.amountCents / 100).toFixed(2) : "");
          }}>
            Combine into one transfer
          </button>
          <button type="button" className="ti-btn ti-btn-light !py-1 !px-3 !text-[0.75rem] !mb-0" onClick={() => setPicked([])}>
            Clear
          </button>
        </div>
      ) : null}
      {combining !== undefined ? (
        <Modal
          open
          title="Combine into one transfer"
          onClose={() => setCombining(undefined)}
          footer={
            <>
              <button type="button" className="ti-btn ti-btn-light" onClick={() => setCombining(undefined)}>
                Cancel
              </button>
              <button
                type="button"
                className="ti-btn ti-btn-primary-full"
                disabled={busy || !(Number(combining) > 0)}
                onClick={() => {
                  setBusy(true);
                  setError(undefined);
                  void api
                    .combineOutgoing(picked, Math.round(Number(combining) * 100))
                    .then((result) => {
                      setNotice(result.message);
                      setPicked([]);
                      setCombining(undefined);
                      return load();
                    })
                    .catch((problem: unknown) => setError(problem instanceof ApiError ? problem.message : "Could not combine them"))
                    .finally(() => setBusy(false));
                }}
              >
                Combine
              </button>
            </>
          }
        >
          <p className="text-[0.813rem]">
            These alerts are one transfer. They become one record, at the earliest time, with what was written on any of them.
          </p>
          <ul className="text-[0.813rem] mb-3">
            {transfers
              ?.filter((one) => picked.includes(one.id))
              .map((one) => (
                <li key={one.id}>
                  {when(one.at)}: -{formatMoney(one.amountCents)}
                </li>
              ))}
          </ul>
          <label htmlFor="combine-amount" className="form-label">The transfer's correct amount S$</label>
          <input id="combine-amount" type="text" inputMode="decimal" className="form-control" value={combining} onChange={(event) => setCombining(event.target.value)} />
        </Modal>
      ) : null}
      {!transfers ? (
        <Working label="Loading" />
      ) : transfers.length === 0 ? (
        <Empty icon="ri-mail-line">{waiting ? "Every alert so far is on an imported statement." : "No alerts yet."}</Empty>
      ) : (
        <Table
          compact
          head={
            <>
              <Th>
                <span className="sr-only">Pick</span>
              </Th>
              <Th>When</Th>
              <Th end>Amount</Th>
              <Th>What it was for</Th>
              <Th>Statement</Th>
            </>
          }
        >
          {transfers.map((transfer) => (
            <tr key={`${transfer.direction}-${transfer.id}`} className="border-b border-defaultborder">
              <td>
                {transfer.direction === "out" ? (
                  <input
                    type="checkbox"
                    className="form-check-input"
                    aria-label={`Pick the ${formatMoney(transfer.amountCents)} transfer of ${when(transfer.at)} to combine`}
                    checked={picked.includes(transfer.id)}
                    onChange={(event) => setPicked((was) => (event.target.checked ? [...was, transfer.id] : was.filter((one) => one !== transfer.id)))}
                  />
                ) : null}
              </td>
              <td className="whitespace-nowrap">{when(transfer.at)}</td>
              <td className={`!text-end font-semibold whitespace-nowrap ${transfer.direction === "in" ? "text-success" : ""}`}>
                {transfer.direction === "in" ? "+" : "-"}
                {formatMoney(transfer.amountCents)}
              </td>
              <td className="min-w-[18rem]">
                {editing?.id === transfer.id ? (
                  <div>
                    <textarea
                      rows={3}
                      autoFocus
                      className="form-control"
                      aria-label="What it was for"
                      placeholder="Who was paid or who paid, what for, and any invoice or buyback number"
                      value={editing.words}
                      onChange={(event) => setEditing({ id: transfer.id, words: event.target.value })}
                    />
                    <div className="flex justify-end gap-2 mt-1">
                      <button type="button" className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0" onClick={() => setEditing(undefined)}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="ti-btn ti-btn-primary-full !py-1 !px-2 !text-[0.75rem] !mb-0"
                        disabled={busy}
                        onClick={() => void save(transfer, editing.words)}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <span className="flex-1 whitespace-pre-line">
                      {transfer.description ??
                        (transfer.saleReference ? (
                          `Sale ${transfer.saleReference}`
                        ) : (
                          <span className="text-warning">Not written yet</span>
                        ))}
                      {transfer.describedBy && transfer.description ? (
                        <span className="block text-[0.6875rem] text-[#8c9097] dark:text-white/50">by {transfer.describedBy}</span>
                      ) : null}
                    </span>
                    <button
                      type="button"
                      className="ti-btn ti-btn-light !py-0.5 !px-2 !text-[0.6875rem] !mb-0 shrink-0"
                      onClick={() => setEditing({ id: transfer.id, words: transfer.description ?? "" })}
                    >
                      <i className="ri-pencil-line" /> {transfer.description ? "Change" : "Write"}
                    </button>
                  </div>
                )}
              </td>
              <td className="whitespace-nowrap">
                {transfer.statementDay ? <Badge tone="success">On the statement, {transfer.statementDay}</Badge> : <Badge tone="warning">Waiting for the statement</Badge>}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Box>
  );
}
